import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Input } from '../input.tsx';
import { OpSubFormProps, OpSubFormRef } from './operationSubForm.ts';


export const ToolNumber = forwardRef<OpSubFormRef, OpSubFormProps>(({
    operation,
    onOperationChange,
}: OpSubFormProps, ref) => {
    const inputRef = useRef<TextSubFormRef>(null);

    useImperativeHandle(ref, () => ({
        validateWarnAndGet: (result) => {
            if (!inputRef.current!.validateAndWarn()) return false;
            result.toolNumber = operation.toolNumber;
            result.details = [...(result.details ?? []), operation.toolNumber!];
            return true;
        }
    }));

    return (
        <TextSubForm
            ref={inputRef}
            label='Tool number'
            type='number'
            step='1'
            min='0'
            minLength={4}
            maxLength={5}
            required={true}
            value={operation.toolNumber?.substring(1)}
            onChange={(value) => onOperationChange({ ...operation, toolNumber: 'T' + value })}
        />
    );
});


export const Offset = forwardRef<OpSubFormRef, OpSubFormProps>(({
    operation,
    onOperationChange,
}: OpSubFormProps, ref) => {
    const inputRef = useRef<TextSubFormRef>(null);

    useImperativeHandle(ref, () => ({
        validateWarnAndGet: (result) => {
            if (!inputRef.current!.validateAndWarn() || !operation.offset || isNaN(parseFloat(operation.offset))) return false;
            result.offset = parseFloat(operation.offset);
            result.details = [...(result.details ?? []), operation.offset];
            return true;
        }
    }));

    return (
        <TextSubForm
            ref={inputRef}
            label='Offset value'
            type='number'
            step='any'
            required={true}
            value={operation.offset}
            onChange={(value) => onOperationChange({ ...operation, offset: value })}
        />
    );
});


export const AdjustmentText = forwardRef<OpSubFormRef, OpSubFormProps>(({
    operation,
    onOperationChange,
}: OpSubFormProps, ref) => {
    const inputRef = useRef<TextSubFormRef>(null);

    useImperativeHandle(ref, () => ({
        validateWarnAndGet: (result) => {
            if (!inputRef.current!.validateAndWarn()) return false;
            result.adjustmentText = operation.adjustmentText;
            result.details = [...(result.details ?? []), (operation.adjustmentText ?? '')];
            return true;
        }
    }));

    return (
        <TextSubForm
            ref={inputRef}
            label='Adjustment'
            type='text'
            required={false}
            value={operation.adjustmentText}
            onChange={(value) => onOperationChange({ ...operation, adjustmentText: value })}
        />
    );
});


export const ProgramEditText = forwardRef<OpSubFormRef, OpSubFormProps>(({
    operation,
    onOperationChange,
}: OpSubFormProps, ref) => {
    const inputRef = useRef<TextSubFormRef>(null);

    useImperativeHandle(ref, () => ({
        validateWarnAndGet: (result) => {
            if (!inputRef.current!.validateAndWarn()) return false;
            result.programEditText = operation.programEditText;
            result.details = [...(result.details ?? []), (operation.programEditText ?? '')];
            return true;
        }
    }));

    return (
        <TextSubForm
            ref={inputRef}
            label='Program edit'
            type='text'
            required={false}
            value={operation.programEditText}
            onChange={(value) => onOperationChange({ ...operation, programEditText: value })}
        />
    );
});


export const PotentialScraps = forwardRef<OpSubFormRef, OpSubFormProps>(({
    operation,
    onOperationChange,
}: OpSubFormProps, ref) => {
    const inputRef = useRef<TextSubFormRef>(null);

    useImperativeHandle(ref, () => ({
        validateWarnAndGet: (result) => {
            if (!inputRef.current!.validateAndWarn()) return false;
            result.potentialScraps = operation.potentialScraps ? parseInt(operation.potentialScraps) : null;
            return true;
        }
    }));

    return (
        <TextSubForm
            ref={inputRef}
            label='Potential scraps'
            type='number'
            step='1'
            min='0'
            required={false}
            value={operation.potentialScraps}
            onChange={(value) => onOperationChange({ ...operation, potentialScraps: value })}
        />
    );
});


export const ConfirmedScraps = forwardRef<OpSubFormRef, OpSubFormProps>(({
    operation,
    onOperationChange,
}: OpSubFormProps, ref) => {
    const inputRef = useRef<TextSubFormRef>(null);

    useImperativeHandle(ref, () => ({
        validateWarnAndGet: (result) => {
            if (!inputRef.current!.validateAndWarn()) return false;
            result.confirmedScraps = operation.confirmedScraps ? parseInt(operation.confirmedScraps) : null;
            return true;
        }
    }));

    return (
        <TextSubForm
            ref={inputRef}
            label='Confirmed scraps'
            type='number'
            step='1'
            min='0'
            required={false}
            value={operation.confirmedScraps}
            onChange={(value) => onOperationChange({ ...operation, confirmedScraps: value })}
        />
    );
});


export const OrderNumber = forwardRef<OpSubFormRef, OpSubFormProps>(({
    operation,
    onOperationChange,
}: OpSubFormProps, ref) => {
    const inputRef = useRef<TextSubFormRef>(null);

    useImperativeHandle(ref, () => ({
        validateWarnAndGet: (result) => {
            if (!inputRef.current!.validateAndWarn()) return false;
            if (operation.potentialScraps && !operation.orderNumber?.length) {
                alert('Order number is required when potential scraps are present');
                return false;
            }

            if (operation.orderNumber) {
                result.orderNumber = operation.orderNumber;
            } else {
                delete result.orderNumber;
            }
            return true;
        }
    }));

    return (
        <TextSubForm
            ref={inputRef}
            label='Order number'
            type='text'
            required={false}
            value={operation.orderNumber}
            onChange={(value) => onOperationChange({ ...operation, orderNumber: value })}
        />
    );
});



interface TextSubFormRef {
    validateAndWarn: () => boolean;
}
interface TextSubFormProps {
    label: string;
    type: 'number' | 'text';
    step?: string;
    min?: string;
    minLength?: number;
    maxLength?: number;
    required: boolean;
    value?: string;
    onChange: (value: string) => void;
}

const TextSubForm = forwardRef<TextSubFormRef, TextSubFormProps>(({
    label,
    type,
    step,
    min,
    minLength,
    maxLength,
    required,
    value,
    onChange,
}, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
        validateAndWarn,
    }));

    const validateAndWarn = () => {
        const errorMessage = validate();
        if (errorMessage) {
            inputRef.current?.setCustomValidity(errorMessage);
            inputRef.current?.reportValidity();
            return false;
        } else {
            return true;
        }
    }

    const validate = () => {
        if (minLength && (!value || value.length < minLength)) {
            if (maxLength) return `Value must be between ${minLength} and ${maxLength} characters`;
            return `Value must be at least ${minLength} characters`;
        }
        if (maxLength && value && value.length > maxLength) {
            if (minLength) return `Value must be between ${minLength} and ${maxLength} characters`;
            return `Value must be at most ${maxLength} characters`;
        }
        return null;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        inputRef.current?.setCustomValidity('');
        onChange(e.target.value);
    }

    return (
        <div className='dialog-form__field--with-margins'>
            <div>{label}</div>
            <Input
                ref={inputRef}
                className='dialog-form__input'
                type={type}
                step={step}
                min={min}
                required={required}
                value={value ?? ''}
                onChange={handleChange}
            />
        </div>
    );
});
